import { useEffect, useState } from 'react'
import { selfServiceApi, deviceToken, type Channel, type OnboardResponse } from '../selfServiceApi'
import { ApiError } from '../api'

/**
 * Public customer profile (route /min-sida, no operator login). Access is tiered:
 *  - a stored trusted-device token resumes the profile straight away (~90 days);
 *  - otherwise, a customer with no verified method yet bootstraps via an AccessKey link
 *    (?key=…) just to verify a first method;
 *  - once any method is verified the AccessKey no longer opens the profile — the customer
 *    logs in with a code to their verified phone/email, which mints the device token.
 * The AccessKey still works on the order page.
 */
export function CustomerSelfService() {
  const [session, setSession] = useState<OnboardResponse | null>(null)
  const [view, setView] = useState<'loading' | 'login' | 'profile'>('loading')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { void boot() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [])

  async function boot() {
    setError(null)
    const stored = deviceToken.get()
    if (stored) {
      try {
        authenticate(await selfServiceApi.resume(stored))
        return
      } catch { deviceToken.clear() /* expired/invalid — fall through to onboarding/login */ }
    }

    const key = new URLSearchParams(window.location.search).get('key')
    if (key?.trim()) {
      try {
        authenticate(await selfServiceApi.onboard(key.trim()))
        return
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) { setView('login'); return } // verified → must log in
        setError(e instanceof ApiError && e.status === 404
          ? 'Den här länken går inte längre att använda. Logga in med ditt mobilnummer eller din e-post.'
          : describe(e))
        setView('login')
        return
      }
    }
    setView('login')
  }

  function authenticate(s: OnboardResponse) {
    deviceToken.set(s.token)
    setSession(s)
    setView('profile')
  }

  function persistToken(token: string) {
    deviceToken.set(token)
    setSession((s) => (s ? { ...s, token } : s))
  }

  function markVerified(channel: Channel) {
    setSession((s) => s && {
      ...s,
      mobilePhoneVerified: channel === 'sms' ? true : s.mobilePhoneVerified,
      emailVerified: channel === 'email' ? true : s.emailVerified,
    })
  }

  function logout() {
    deviceToken.clear()
    setSession(null)
    setView('login')
  }

  return (
    <div className="center-screen">
      <div className="card" style={{ maxWidth: 460 }}>
        <h1>Mina uppgifter</h1>
        {error && <p className="error">{error}</p>}
        {view === 'loading' && <p className="muted">Laddar…</p>}
        {view === 'login' && <LoginForm onAuthenticated={authenticate} />}
        {view === 'profile' && session && (
          <Profile session={session} onToken={persistToken} onVerified={markVerified} onLogout={logout} />
        )}
      </div>
    </div>
  )
}

function LoginForm({ onAuthenticated }: { onAuthenticated: (s: OnboardResponse) => void }) {
  const [identifier, setIdentifier] = useState('')
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'idle' | 'sent'>('idle')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function sendCode() {
    if (!identifier.trim()) return
    setBusy(true); setError(null); setInfo(null)
    try {
      await selfServiceApi.loginRequestCode(identifier.trim())
      setStage('sent')
      setInfo('Om uppgiften finns registrerad och verifierad har vi skickat en engångskod.')
    } catch (e) { setError(describe(e)) } finally { setBusy(false) }
  }

  async function login() {
    setBusy(true); setError(null)
    try { onAuthenticated(await selfServiceApi.loginVerify(identifier.trim(), code.trim())) }
    catch (e) { setError(describe(e)) } finally { setBusy(false) }
  }

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Logga in</h2>
      <p className="muted">
        Logga in för att se och uppdatera dina uppgifter. Ange ditt verifierade mobilnummer eller din
        e-postadress, så skickar vi en engångskod.
      </p>
      <div className="row-form">
        <input placeholder="Mobilnummer eller e-post" value={identifier}
          onChange={(e) => { setIdentifier(e.target.value); setStage('idle') }}
          onKeyDown={(e) => e.key === 'Enter' && void sendCode()} />
        <button disabled={busy || !identifier.trim()} onClick={() => void sendCode()}>Skicka kod</button>
      </div>
      {stage === 'sent' && (
        <div className="row-form" style={{ marginTop: '0.5rem' }}>
          <input placeholder="Inloggningskod" value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void login()} />
          <button className="primary" disabled={busy || !code.trim()} onClick={() => void login()}>Logga in</button>
        </div>
      )}
      {info && <p className="muted">{info}</p>}
      {error && <p className="error">{error}</p>}
    </>
  )
}

function Profile({ session, onToken, onVerified, onLogout }: {
  session: OnboardResponse; onToken: (token: string) => void
  onVerified: (channel: Channel) => void; onLogout: () => void
}) {
  const hasCredential = session.mobilePhoneVerified || session.emailVerified

  return (
    <>
      {hasCredential ? (
        <p className="muted">
          Hej {session.firstName}! Här håller du dina kontaktuppgifter aktuella. Vi skickar en
          verifieringskod när du ändrar en uppgift.
        </p>
      ) : (
        <div style={{ borderLeft: '3px solid var(--accent)', background: 'var(--bg)',
          border: '1px solid var(--border)', borderLeftWidth: 3, borderLeftColor: 'var(--accent)',
          borderRadius: 6, padding: '0.6rem 0.9rem', marginBottom: '0.5rem' }}>
          <p style={{ margin: '0 0 0.25rem' }}><strong>Hej {session.firstName}! Skapa din inloggning.</strong></p>
          <p className="muted" style={{ margin: 0 }}>
            Lägg till och verifiera minst en uppgift — ditt mobilnummer eller din e-post. Ange en uppgift
            nedan så skickar vi en verifieringskod. Den blir din inloggning nästa gång du besöker sidan.
          </p>
        </div>
      )}
      <VerifyField token={session.token} channel="sms" label="Mobilnummer"
        placeholder="07XXXXXXXX" initial={session.mobilePhone ?? ''}
        verified={session.mobilePhoneVerified}
        lockedUntil={session.mobilePhoneChangeAllowedUtc} onToken={onToken} onVerified={onVerified} />
      <VerifyField token={session.token} channel="email" label="E-post"
        placeholder="namn@exempel.se" initial={session.email ?? ''}
        verified={session.emailVerified} onToken={onToken} onVerified={onVerified} />
      {hasCredential && (
        <p className="muted" style={{ marginTop: '1.25rem' }}>
          <button onClick={onLogout}>Logga ut</button>
        </p>
      )}
    </>
  )
}

function VerifyField({ token, channel, label, placeholder, initial, verified, lockedUntil, onToken, onVerified }: {
  token: string; channel: Channel; label: string; placeholder: string; initial: string
  verified?: boolean; lockedUntil?: string | null
  onToken?: (token: string) => void; onVerified?: (channel: Channel) => void
}) {
  const [value, setValue] = useState(initial)
  // The last value confirmed/saved for this channel; the field is "verified" while the
  // input still equals it. Editing the value makes it dirty → the Skicka kod flow returns.
  const [savedValue, setSavedValue] = useState(initial)
  const [isVerified, setIsVerified] = useState(verified ?? false)
  const [code, setCode] = useState('')
  const [stage, setStage] = useState<'idle' | 'sent'>('idle')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // For SMS: the instant the number may next be changed. Until then the field is locked
  // read-only with a live countdown. An admin reset in the CRM clears this server-side.
  const [lockUntil, setLockUntil] = useState<string | null>(lockedUntil ?? null)
  const remainingMs = useCountdown(lockUntil)
  const locked = remainingMs > 0

  const dirty = value.trim() !== savedValue.trim()
  const showVerified = isVerified && !dirty

  async function send() {
    if (!value.trim()) return
    setBusy(true); setError(null)
    try { await selfServiceApi.requestCode(token, channel, value.trim()); setStage('sent') }
    catch (e) { setError(describe(e)) } finally { setBusy(false) }
  }

  async function verify() {
    setBusy(true); setError(null)
    try {
      const res = await selfServiceApi.verify(token, channel, value.trim(), code.trim())
      setSavedValue(value.trim())
      setIsVerified(true)
      setStage('idle'); setCode('')
      if (channel === 'sms') setLockUntil(res.mobilePhoneChangeAllowedUtc)
      onToken?.(res.token) // refresh the trusted-device token
      onVerified?.(channel) // this channel is now a usable login credential
    } catch (e) { setError(describe(e)) } finally { setBusy(false) }
  }

  // Verified mobile inside its 24h change window: read-only with a countdown until it
  // can be changed again.
  if (locked && !dirty) {
    return (
      <div className="field" style={{ marginTop: '1rem' }}>
        <span className="field-label">{label}</span>
        <div className="row-form">
          <input value={value} readOnly disabled />
          <span className="badge">✓ Verifierat</span>
        </div>
        <p className="muted">Du kan ändra ditt mobilnummer igen om {formatCountdown(remainingMs)}.</p>
      </div>
    )
  }

  return (
    <div className="field" style={{ marginTop: '1rem' }}>
      <span className="field-label">{label}</span>
      <div className="row-form">
        <input placeholder={placeholder} value={value}
          onChange={(e) => { setValue(e.target.value); setStage('idle'); setCode('') }} />
        {showVerified
          ? <span className="badge">✓ Verifierat</span>
          : <button disabled={busy || !value.trim()} onClick={() => void send()}>Skicka kod</button>}
      </div>
      {stage === 'sent' && !showVerified && (
        <div className="row-form" style={{ marginTop: '0.5rem' }}>
          <input placeholder="Verifieringskod" value={code} onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void verify()} />
          <button className="primary" disabled={busy || !code.trim()} onClick={() => void verify()}>Verifiera</button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  )
}

/** Milliseconds remaining until `target` (ISO), re-rendering every second. 0 when null/elapsed. */
function useCountdown(target: string | null): number {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!target) return
    const id = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(id)
  }, [target])
  return target ? Math.max(0, new Date(target).getTime() - Date.now()) : 0
}

function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':')
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    // The API returns { error: "..." } (Swedish) for verification failures — surface it directly.
    try {
      const parsed = JSON.parse(e.body) as { error?: string }
      if (parsed?.error) return parsed.error
    } catch { /* not JSON */ }
    return `Fel ${e.status}: ${e.body || 'okänt fel'}`
  }
  return e instanceof Error ? e.message : String(e)
}
